import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { getDomain, retryDomain } from "@/api/domains";
import { Page } from "@/components/page";
import { StatusBadge } from "@/components/status-badge";
import { RelativeTime } from "@/components/relative-time";
import { EmptyState } from "@/components/empty-state";

export function DomainDetailPage() {
  const { domainId } = useParams({ strict: false }) as { domainId: string };
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ["domain", domainId],
    queryFn: () => getDomain(domainId),
  });

  const retry = useMutation({
    mutationFn: () => retryDomain(domainId),
    onSuccess: () => {
      toast.success("Re-enqueued for processing");
      qc.invalidateQueries({ queryKey: ["domain", domainId] });
    },
  });

  if (detail.isLoading) {
    return <Page title="Domain"><div className="card p-6 text-muted text-sm">Loading…</div></Page>;
  }
  if (!detail.data) {
    return <Page title="Domain"><div className="card p-6 text-danger text-sm">Domain not found.</div></Page>;
  }

  const { domain: d, latest_scan, events } = detail.data;

  return (
    <Page
      title={d.normalized_url}
      actions={
        <>
          <Link to="/domains" className="btn-ghost text-sm">
            <ArrowLeft className="h-4 w-4" /> All domains
          </Link>
          <button
            className="btn"
            onClick={() => retry.mutate()}
            disabled={retry.isPending}
          >
            <RefreshCw className={retry.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            Retry
          </button>
        </>
      }
    >
      <section className="card p-4">
        <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
          <Meta label="URL">{d.url ?? "—"}</Meta>
          <Meta label="Status"><StatusBadge status={d.status} title={d.init_error ?? undefined} /></Meta>
          <Meta label="Tags">{d.tags.length === 0 ? "—" : d.tags.join(", ")}</Meta>
          <Meta label="Run">
            {d.run_id ? (
              <Link to="/runs/$runId" params={{ runId: d.run_id }} className="text-accent hover:underline">
                {d.run_id}
              </Link>
            ) : "—"}
          </Meta>
          <Meta label="Last scanned"><RelativeTime iso={d.last_scanned_at} /></Meta>
          <Meta label="Fetch error">
            <span className="font-mono text-xs">{d.init_error ?? "—"}</span>
          </Meta>
          <Meta label="Unreachable reason">
            {d.unreachable_reason ? (
              <span className="badge text-[10px] font-mono">
                {d.unreachable_reason}
              </span>
            ) : (
              "—"
            )}
          </Meta>
        </div>
      </section>

      <section className="card p-4">
        <h2 className="text-sm font-semibold mb-3">Latest scan</h2>
        {latest_scan ? (
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
            <Meta label="Action kind">{latest_scan.action_kind ?? "—"}</Meta>
            <Meta label="Check">{latest_scan.check_name ?? "—"}{latest_scan.check_version ? ` (v${latest_scan.check_version})` : ""}</Meta>
            <Meta label="Tags">{latest_scan.tags.length === 0 ? "—" : latest_scan.tags.join(", ")}</Meta>
            <Meta label="Scanned"><RelativeTime iso={latest_scan.scanned_at} /></Meta>
            <details className="sm:col-span-2 mt-2">
              <summary className="cursor-pointer text-xs text-muted hover:text-fg">Evidence + matches (raw JSON)</summary>
              <pre className="mt-2 p-3 rounded-md bg-subtle text-xs overflow-auto max-h-[24rem]">
                {JSON.stringify({ matches: latest_scan.matches, evidence: latest_scan.evidence }, null, 2)}
              </pre>
            </details>
          </div>
        ) : <p className="text-sm text-muted">No scan result yet.</p>}
      </section>

      <section className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-sm font-semibold">Events</div>
        {events.length === 0 ? (
          <EmptyState title="No events" />
        ) : (
          <div className="overflow-x-auto max-h-[40rem]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="text-xs text-muted">
                  <th className="px-4 py-2 text-left font-medium">At</th>
                  <th className="px-4 py-2 text-left font-medium">Stage</th>
                  <th className="px-4 py-2 text-left font-medium">Event</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {events.map((e, i) => (
                  <tr key={i} className="hover:bg-subtle/40">
                    <td className="px-4 py-1.5 text-muted text-xs"><RelativeTime iso={e.at} /></td>
                    <td className="px-4 py-1.5 text-muted">{e.stage ?? "—"}</td>
                    <td className="px-4 py-1.5 font-mono text-xs">{e.event}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Page>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted">{label}</span>
      <span className="break-all">{children}</span>
    </div>
  );
}
